import {
  DollarSign,
  Edit,
  Image,
  Layers,
  LogOut,
  Search,
  Settings,
  Star,
  Users
} from 'react-feather';

import { useMobile } from '@/hooks';
import { useAppContext } from '@/context';
import { isProduction } from '@/utils/environment';
import { getDiscoveryBaseUrl } from '@/utils/discovery';
import HeartIcon from '../../../public/assets/icons/hand-heart.svg';

/** Discovery nav is shown for this team even when has_discovery_enabled is false (BO flag bypass). */
const DISCOVERY_ALWAYS_VISIBLE_TEAM_ID = '6lmlprWT-tea';

const canSeeDiscoveryNav = (user: Api.UserObject): boolean => {
  if (!user.is_client || !getDiscoveryBaseUrl()) {
    return false;
  }
  if (user.team?.team_id === DISCOVERY_ALWAYS_VISIBLE_TEAM_ID) {
    return true;
  }
  return user.team?.has_discovery_enabled === true;
};

const canSeeAmbassadors = (user: Api.UserObject): boolean => {
  // Always show in non-production environments
  if (!isProduction()) return true;

  // In production: show for @beatly.com emails or teams containing "merrell"
  const isBeatlyEmail = user.email?.toLowerCase().endsWith('@beatly.com') || false;
  const isMerrellTeam = user.team?.name?.toLowerCase().includes('merrell') || false;

  return isBeatlyEmail || isMerrellTeam;
};

export const NAVIGATION_LINKS_PROPS = (isMobile: boolean) => ({
  size: isMobile ? 16 : 20
});

interface NavigationLinksProps {
  user: Api.UserObject;
  id?: string | string[];
  isIGC?: boolean;
  campaign?: Api.Client.Campaign;
}

export function NavigationLinks({ user, id, campaign: campaignProp }: NavigationLinksProps) {
  const isMobile = useMobile();
  const { campaign: contextCampaign } = useAppContext();

  // Use campaign prop if provided, otherwise fall back to context
  const campaign = campaignProp || contextCampaign;

  return [
    {
      id: 'campaigns',
      href: '/connect/campaigns',
      icon: <Layers {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      subnav: id && [
        {
          id: 'Overview',
          href: `/connect/campaigns/${id}`
        },
        ...(campaign?.has_curation
          ? [
              {
                id: 'Curation',
                href: `/connect/campaigns/${id}/curation`
              }
            ]
          : []),
        {
          id: 'Collaborations',
          href: `/connect/campaigns/${id}/collaborations`
        },
        {
          id: 'Drafts',
          href: `/connect/campaigns/${id}/drafts`
        },
        ...(campaign?.has_summary
          ? [
              {
                id: 'Summary',
                href: `/connect/campaigns/${id}/summary`
              }
            ]
          : []),
        ...(campaign?.sentiment_app_url
          ? [
              {
                id: 'Sentiment',
                href: `/connect/campaigns/${id}/sentiment`
              }
            ]
          : [])
      ],
      hide: user.is_influencer
    },
    {
      id: 'collaborations',
      href: '/connect/collaborations',
      icon: <Layers {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: user.is_client
      // subnav:
      //   id &&
      //   [
      //     {
      //       id: 'Details',
      //       href: `/connect/collaborations/${id}`
      //     },
      //     {
      //       id: 'Drafts',
      //       href: `/connect/collaborations/${id}/drafts`
      //     },
      //     {
      //       id: 'Publications',
      //       href: `/connect/collaborations/${id}/publications`,
      //       hide: isIGC
      //     },
      //     {
      //       id: 'Screenshots',
      //       href: `/connect/collaborations/${id}/statistics`,
      //       hide: isIGC
      //     }
      //   ].filter((permission) => !permission.hide)
    },
    {
      id: 'plans',
      href: '/connect/plans',
      icon: <Edit {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: !user.is_client
    },
    {
      id: 'contentHub',
      href: '#',
      // eslint-disable-next-line jsx-a11y/alt-text
      icon: <Image {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: !user.is_client
      // hide: !user.permissions?.show_menu_content_hub || !user.team?.content_hub_url
    },
    {
      id: 'discovery',
      href: '#',
      icon: <Search {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: !canSeeDiscoveryNav(user)
    },
    {
      id: 'ambassadors',
      href: '/connect/ambassadors',
      icon: <Star {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: !user.is_client || !canSeeAmbassadors(user)
    },
    {
      id: 'sentiment',
      href: '/connect/sentiment',
      // eslint-disable-next-line jsx-a11y/alt-text
      icon: (
        <HeartIcon
          style={{ width: '20px', height: '20px' }}
          {...NAVIGATION_LINKS_PROPS(isMobile)}
        />
      ),
      hide: !user.team?.sentiment_app_url
    },
    {
      id: 'team',
      href: '/connect/team',
      icon: <Users {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: !user.permissions?.show_menu_team
    },
    {
      id: 'payouts',
      href: '/connect/payouts',
      icon: <DollarSign {...NAVIGATION_LINKS_PROPS(isMobile)} />,
      hide: !user.permissions?.show_menu_payouts
    },
    {
      id: 'settings',
      href: '/connect/settings',
      icon: <Settings {...NAVIGATION_LINKS_PROPS(isMobile)} />
    },
    {
      id: 'logout',
      href: '/logout',
      icon: <LogOut {...NAVIGATION_LINKS_PROPS(isMobile)} />
    }
  ].filter((permission) => !permission.hide);
}
